import React, { useEffect, useState } from "react";
import { Users, Plus, Edit2, Trash2, Search, X } from "lucide-react";
import "./ClientsPage.css";

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    contact_person: "",
  });
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");

  // ✅ Fetch Clients
  async function fetchClients() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/clients`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      const data = await res.json();
      setClients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching clients:", err);
      setClients([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchClients();
  }, []);

  // ✅ Handle input changes
  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  // ✅ Add / Update client
  async function handleSubmit(e) {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const url = editingClient
        ? `${process.env.REACT_APP_API_URL}/api/clients/${editingClient.id}`
        : `${process.env.REACT_APP_API_URL}/api/clients`;
      const method = editingClient ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        body: JSON.stringify(form),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save client");

      if (editingClient) {
        setClients((prev) =>
          prev.map((c) => (c.id === editingClient.id ? data : c))
        );
        setMessage("✅ Client updated successfully!");
      } else {
        setClients((prev) => [...prev, data]);
        setMessage("✅ Client added successfully!");
      }

      setShowModal(false);
      setEditingClient(null);
      setForm({
        name: "",
        email: "",
        phone: "",
        address: "",
        contact_person: "",
      });
    } catch (err) {
      setMessage(`❌ ${err.message}`);
    }
  }

  // ✅ Delete client
  async function handleDelete(id) {
    if (!window.confirm("Are you sure you want to delete this client?")) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/clients/${id}`, {
        method: "DELETE",
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to delete client");

      setClients((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      alert(`❌ ${err.message}`);
    }
  }

  // ✅ Edit client
  function handleEdit(client) {
    setEditingClient(client);
    setForm({
      name: client.name,
      email: client.email,
      phone: client.phone,
      address: client.address,
      contact_person: client.contact_person,
    });
    setShowModal(true);
  }

  // ✅ Filter clients (search)
  const filteredClients = clients.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      c.phone.toLowerCase().includes(search.toLowerCase()) ||
      (c.address && c.address.toLowerCase().includes(search.toLowerCase())) ||
      (c.contact_person &&
        c.contact_person.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="clients-container">
      {/* ===== Header Section ===== */}
      <div className="clients-header-row">
        <div className="clients-header-left">
          {/* <Users className="clients-header-icon" size={24} /> */}
          <h2>Nam-Mark Clients</h2>
        </div>

        <div className="clients-header-actions">
          {/* ✅ Search Bar with Icon Inside */}
          <div className="clients-search">
            <Search className="clients-search-icon" size={18} />
            <input
              type="text"
              placeholder="Search clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="clients-search-input"
            />
          </div>

          <button
            className="clients-add-btn"
            onClick={() => {
              setShowModal(true);
              setEditingClient(null);
              setForm({
                name: "",
                email: "",
                phone: "",
                address: "",
                contact_person: "",
              });
            }}
          >
            <Plus size={18} /> Add Client
          </button>
        </div>
      </div>

      {/* ===== Clients Table ===== */}
      {loading ? (
        <p>Loading clients...</p>
      ) : (
        <table className="clients-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Client Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Address</th>
              <th>Contact Person</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.length > 0 ? (
              filteredClients.map((c, index) => (
                <tr key={c.id}>
                  <td>{index + 1}</td>
                  <td>{c.name}</td>
                  <td>{c.email}</td>
                  <td>{c.phone}</td>
                  <td>{c.address}</td>
                  <td>{c.contact_person}</td>
                  <td>
                    <button
                      className="clients-edit-btn"
                      onClick={() => handleEdit(c)}
                    >
                      <Edit2 size={16} /> Edit
                    </button>
                    <button
                      className="clients-delete-btn"
                      onClick={() => handleDelete(c.id)}
                    >
                      <Trash2 size={16} /> Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" style={{ textAlign: "center" }}>
                  No clients found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* ===== Add/Edit Modal ===== */}
      {showModal && (
        <div className="clients-modal-overlay">
          <div className="clients-modal">
            <div className="clients-modal-header">
              <h3>{editingClient ? "Edit Client" : "Add New Client"}</h3>
              {/* <button
                className="clients-close-btn"
                onClick={() => {
                  setShowModal(false);
                  setEditingClient(null);
                  setMessage("");
                }}
              >
                <X size={18} />
              </button> */}
            </div>

            {message && (
              <div
                className={`clients-message ${
                  message.startsWith("✅") ? "success" : "error"
                }`}
              >
                {message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="client-form">
              <input
                type="text"
                name="name"
                placeholder="Client Name"
                value={form.name}
                onChange={handleChange}
                required
              />
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={form.email}
                onChange={handleChange}
              />
              <input
                type="text"
                name="phone"
                placeholder="Phone"
                value={form.phone}
                onChange={handleChange}
              />
              <input
                type="text"
                name="address"
                placeholder="Address"
                value={form.address}
                onChange={handleChange}
              />
              <input
                type="text"
                name="contact_person"
                placeholder="Contact Person"
                value={form.contact_person}
                onChange={handleChange}
              />

              <div className="clients-modal-actions">
                <button type="submit">
                  {editingClient ? "Update Client" : "Add Client"}
                </button>
                <button
                  type="button"
                  className="clients-cancel-btn"
                  onClick={() => {
                    setShowModal(false);
                    setEditingClient(null);
                    setMessage("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
